/**
 * Boş səhifə yoxlaması nəticələrini ZIP olaraq endirir.
 * ZIP içində iki qovluq: admin bos tesdiqlendi, admin bos redd edildi.
 * Fayl adları: name_surname_studentid_questionnumber.jpg
 */

import JSZip from 'jszip';
import { ref, getBlob } from 'firebase/storage';
import { storage } from '../firebase';

/** Boş səhifə yoxlaması üçün minimal maddə (StudentResultsPage emptyPagesToVerify ilə uyğun) */
export interface EmptyPageExportItem {
  studentId: string;
  studentName: string;
  questionNumber: string;
  questionData: { path?: string };
  verificationStatus?: 'pending' | 'confirmed' | 'rejected';
}

export const FOLDER_CONFIRMED = 'admin bos tesdiqlendi';
export const FOLDER_REJECTED = 'admin bos redd edildi';

/** Path-in Firebase Storage full URL olub-olmadığını yoxlayır */
function isFirebaseStorageUrl(path: string): boolean {
  return typeof path === 'string' && (
    path.startsWith('https://firebasestorage.googleapis.com') ||
    path.startsWith('https://') && path.includes('firebasestorage')
  );
}

/** Firebase Storage URL-dən path hissəsini çıxarır */
function extractPathFromFirebaseUrl(url: string): string | null {
  try {
    const match = url.match(/\/o\/([^?]+)/);
    if (match && match[1]) return decodeURIComponent(match[1]);
    return null;
  } catch {
    return null;
  }
}

/** Path-dən (URL və ya storage path) şəkil blob-u alır. CORS yoxdur – Firebase SDK getBlob istifadə edir. */
async function getImageBlob(path: string): Promise<Blob> {
  if (!path || typeof path !== 'string') {
    throw new Error('Path boşdur');
  }
  const storagePath = isFirebaseStorageUrl(path)
    ? extractPathFromFirebaseUrl(path)
    : path;
  if (!storagePath) {
    throw new Error('Storage path alına bilmədi');
  }
  const storageRef = ref(storage, storagePath);
  const blob = await getBlob(storageRef);
  return blob;
}

/** Fayl adı üçün təhlükəsiz string: name_surname_studentid_questionnumber.jpg */
export function safeFileName(item: EmptyPageExportItem): string {
  const namePart = (item.studentName || 'unknown')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '') || 'unknown';
  const studentId = (item.studentId || '').replace(/[\\/:*?"<>|]/g, '_');
  const qNum = (item.questionNumber || '').replace(/[\\/:*?"<>|]/g, '_');
  return `${namePart}_${studentId}_${qNum}.jpg`;
}

export interface EmptyPageZipExportOptions {
  /** Boş səhifə siyahısı (yalnız confirmed və rejected olanlar daxil edilir) */
  emptyPages: EmptyPageExportItem[];
  /** ZIP fayl adı (suffix tarix/saat ilə əlavə olunur) */
  zipFileName?: string;
  /** Tərəqqi callback: (current, total, message) */
  onProgress?: (current: number, total: number, message: string) => void;
}

export interface EmptyPageZipExportResult {
  success: boolean;
  totalAdded: number;
  confirmedCount: number;
  rejectedCount: number;
  errors: string[];
}

/**
 * Boş səhifə nəticələrini (təsdiqlənən və rədd edilən) JPG şəkillər olaraq
 * ZIP faylında endirir. ZIP içində iki qovluq: admin bos tesdiqlendi, admin bos redd edildi.
 */
export async function downloadEmptyPageResultsAsZip(
  options: EmptyPageZipExportOptions
): Promise<EmptyPageZipExportResult> {
  const { emptyPages, zipFileName = 'bos_sehife_netice', onProgress } = options;
  const result: EmptyPageZipExportResult = {
    success: false,
    totalAdded: 0,
    confirmedCount: 0,
    rejectedCount: 0,
    errors: [],
  };

  const toExport = emptyPages.filter(
    (p) => p.verificationStatus === 'confirmed' || p.verificationStatus === 'rejected'
  );

  if (toExport.length === 0) {
    result.errors.push('Export üçün heç bir təsdiqlənmiş və ya rədd edilmiş səhifə yoxdur.');
    return result;
  }

  const zip = new JSZip();
  const folderConfirmed = zip.folder(FOLDER_CONFIRMED)!;
  const folderRejected = zip.folder(FOLDER_REJECTED)!;

  let added = 0;
  const total = toExport.length;

  for (let i = 0; i < toExport.length; i++) {
    const item = toExport[i];
    const path = item.questionData?.path;
    if (!path) {
      result.errors.push(`${item.studentName} / ${item.questionNumber}: path yoxdur`);
      if (onProgress) onProgress(i + 1, total, `Atlandı: path yoxdur`);
      continue;
    }

    try {
      if (onProgress) onProgress(i + 1, total, `${item.studentName} - ${item.questionNumber}`);
      const blob = await getImageBlob(path);
      const fileName = safeFileName(item);
      const folder = item.verificationStatus === 'confirmed' ? folderConfirmed : folderRejected;
      folder.file(fileName, blob, { binary: true });
      added++;
      if (item.verificationStatus === 'confirmed') result.confirmedCount++;
      else result.rejectedCount++;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      result.errors.push(`${item.studentName} / ${item.questionNumber}: ${msg}`);
    }
  }

  result.totalAdded = added;

  if (added === 0) {
    result.errors.push('Heç bir şəkil ZIP-ə əlavə edilə bilmədi.');
    return result;
  }

  try {
    const blob = await zip.generateAsync({ type: 'blob' });
    const name = `${zipFileName}_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '_')}.zip`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    result.success = true;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    result.errors.push(`ZIP yaradılarkən xəta: ${msg}`);
  }

  return result;
}

/**
 * Şəkil elementindən (DOM-da artıq yüklənmiş) canvas ilə blob alır.
 * CORS: şəkil crossOrigin="anonymous" ilə yüklənməli və server CORS header göndərməlidir.
 */
function imageElementToBlob(img: HTMLImageElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (!img.naturalWidth || !img.naturalHeight) {
      reject(new Error('Şəkil hələ yüklənməyib'));
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Canvas context alına bilmədi'));
      return;
    }
    try {
      ctx.drawImage(img, 0, 0);
    } catch (e) {
      reject(new Error('CORS: Şəkil canvas-a çəkilə bilmədi'));
      return;
    }
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('toBlob uğursuz'));
      },
      'image/jpeg',
      0.92
    );
  });
}

export interface EmptyPageZipFromImagesOptions {
  /** Təsdiqlənən / rədd edilən maddələr (sıra eyni qalsın) */
  items: EmptyPageExportItem[];
  /** Hər maddə üçün uyğun img elementini qaytarır (yoxdursa null) */
  getImageElement: (item: EmptyPageExportItem) => HTMLImageElement | null;
  zipFileName?: string;
  onProgress?: (current: number, total: number, message: string) => void;
}

/**
 * UI-da artıq yüklənmiş img elementlərindən ZIP yaradır (CORS-siz, şəkillər səhifədə göstərildikdən sonra).
 * ZIP içində iki qovluq: admin bos tesdiqlendi, admin bos redd edildi.
 */
export async function downloadEmptyPageResultsAsZipFromImages(
  options: EmptyPageZipFromImagesOptions
): Promise<EmptyPageZipExportResult> {
  const { items, getImageElement, zipFileName = 'bos_sehife_netice', onProgress } = options;
  const result: EmptyPageZipExportResult = {
    success: false,
    totalAdded: 0,
    confirmedCount: 0,
    rejectedCount: 0,
    errors: [],
  };

  if (items.length === 0) {
    result.errors.push('Export üçün heç bir maddə yoxdur.');
    return result;
  }

  const zip = new JSZip();
  const folderConfirmed = zip.folder(FOLDER_CONFIRMED)!;
  const folderRejected = zip.folder(FOLDER_REJECTED)!;
  const total = items.length;
  let added = 0;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (onProgress) onProgress(i + 1, total, `${item.studentName} - ${item.questionNumber}`);
    const img = getImageElement(item);
    if (!img || !img.complete || !img.naturalWidth) {
      result.errors.push(`${item.studentName} / ${item.questionNumber}: şəkil yoxdur və ya yüklənməyib`);
      continue;
    }
    try {
      const blob = await imageElementToBlob(img);
      const fileName = safeFileName(item);
      const folder = item.verificationStatus === 'confirmed' ? folderConfirmed : folderRejected;
      folder.file(fileName, blob, { binary: true });
      added++;
      if (item.verificationStatus === 'confirmed') result.confirmedCount++;
      else result.rejectedCount++;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      result.errors.push(`${item.studentName} / ${item.questionNumber}: ${msg}`);
    }
  }

  result.totalAdded = added;
  if (added === 0) {
    result.errors.push('Heç bir şəkil ZIP-ə əlavə edilə bilmədi (CORS və ya yüklənmə problemi).');
    return result;
  }

  try {
    const blob = await zip.generateAsync({ type: 'blob' });
    const name = `${zipFileName}_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '_')}.zip`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
    result.success = true;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    result.errors.push(`ZIP yaradılarkən xəta: ${msg}`);
  }
  return result;
}
